import {
    createAsyncThunk,
    createSlice,
    EntityId,
    PayloadAction,
} from "@reduxjs/toolkit";

import { DateTime } from "luxon";

import { RootState } from "app/store";
import { initialState, persistedRecordingAdapter } from "./state";
import { caseViewerSelectors } from "./selectors";
import { playbackSelectors } from "./selectors.playback";

import { caseManagerApi } from "services/streamingApi/caseManager.api";

import UserSettings from "features/user/types/UserSettings";
import { DossierDeviceStatusChanged } from "features/cases/types/DossierDeviceStatusChanged";
import { PersistedRecordingPersistedStartAndStopTimeChanged } from "features/cases/types/PersistedRecordingPersistedStartAndStopTimeChanged";
import { DossierInformationChanged } from "features/cases/types/DossierInformationChanged";

import { FetchPersistedRecordingsForDossierDeviceRejectedReason } from "../types/FetchPersistedRecordingsForDossierDeviceRejectedReason";
import { PersistedRecordingStatusChanged } from "../../types/PersistedRecordingStatusChanged";
import { DossierDeviceStatus } from "types/DossierDeviceStatus";
import { luxonUtils } from "utils/luxonUtils";

// thunks
const loadDossierAsync = createAsyncThunk(
    "caseViewer/loadDossierAsync",
    async (
        payload: {
            dossierId: EntityId;
        },
        { rejectWithValue, dispatch },
    ) => {
        // reset state
        await dispatch(caseViewerSlice.actions.resetState());

        const { data, error, isSuccess } = await dispatch(
            caseManagerApi.endpoints.getDossierById.initiate(payload.dossierId),
        );

        if (isSuccess) {
            return data;
        }

        return rejectWithValue(
            `Error while retrieving dossier with id ${payload.dossierId}: ${JSON.stringify(error)}`,
        );
    },
);

export const fetchPersistedRecordingsForDossierDeviceAsync = createAsyncThunk(
    "caseViewer/fetchPersistedRecordingsForDossierDeviceAsync",
    async (
        payload: {
            dossierDeviceId: EntityId;
        },
        { rejectWithValue, dispatch, getState },
    ) => {
        const state = getState() as RootState;
        const dossier = caseViewerSelectors.selectDossier(state);

        if (!dossier) {
            return rejectWithValue({
                message: "Dossier can't be undefined",
            } as FetchPersistedRecordingsForDossierDeviceRejectedReason);
        }

        // fetch persisted recordings for dossier device from API
        const { data, error, isSuccess } = await dispatch(
            caseManagerApi.endpoints.getPersistedRecordingsByDossierDeviceId.initiate(
                {
                    dossierId: dossier?.id,
                    dossierDeviceId: payload.dossierDeviceId,
                },
            ),
        );

        if (isSuccess) {
            return data;
        }

        return rejectWithValue({
            message: `Error while retrieving persisted recordings for dossier device ${payload.dossierDeviceId}: ${error}`,
        } as FetchPersistedRecordingsForDossierDeviceRejectedReason);
    },
);

export const removePersistedRecordingsAndStateForInvisibleDossierDevicesAsync =
    createAsyncThunk(
        "caseViewer/removePersistedRecordingsAndStateForInvisibleDossierDevicesAsync",
        async (payload: unknown, { getState }) => {
            // fetch persisted recording ids for INvisible dossier devices from state
            const state = getState() as RootState;

            return playbackSelectors.selectAllPersistedRecordingIdsForInvisibleDossierDevices(
                state,
            );
        },
    );

// slice
export const caseViewerSlice = createSlice({
    name: "caseViewer",
    initialState,
    reducers: {
        resetState: () => {
            return { ...initialState };
        },
        setVisibilityDossierDevices: (
            state,
            action: PayloadAction<{
                dossierDeviceIds: EntityId[];
                visible: boolean;
            }>,
        ) => {
            if (action.payload.visible) {
                state.visibleDossierDeviceIds = action.payload.dossierDeviceIds;
            } else if (!action.payload.visible) {
                // device unvisible
                state.visibleDossierDeviceIds = [];
            }
        },
        upsertVisibleDossierDeviceIdState: (
            state,
            action: PayloadAction<{
                dossierDeviceId: EntityId;
                visible: boolean;
            }>,
        ) => {
            if (action.payload.visible) {
                // new dossier device visible
                state.visibleDossierDeviceIds.unshift(
                    action.payload.dossierDeviceId,
                );
            } else if (!action.payload.visible) {
                // device unvisible
                state.visibleDossierDeviceIds =
                    state.visibleDossierDeviceIds.filter(
                        (x) => x !== action.payload.dossierDeviceId,
                    );
            }
        },
        setVisibilityAllDossierDevices: (
            state,
            action: PayloadAction<{
                visible: boolean;
            }>,
        ) => {
            if (action.payload.visible) {
                state.visibleDossierDeviceIds =
                    state.dossier?.dossierDevices
                        .filter(
                            (x) =>
                                x.isSelected &&
                                x.status === DossierDeviceStatus.Ready,
                        )
                        .map((x) => x.id) ?? [];
            } else {
                state.visibleDossierDeviceIds = [];
            }
        },
        setElapsedPlayTimeInMs: (
            state,
            action: PayloadAction<{ elapsedPlayTimeInMs: number }>,
        ) => {
            state.playbackElapsedTimeInMs = action.payload.elapsedPlayTimeInMs;
        },
        startPlayback: (state) => {
            let newStartPos;

            if (!state.playbackStartDateTime) {
                newStartPos = state.dossier?.startTime;
            } else {
                newStartPos = luxonUtils
                    .fromIsoWithFallback(
                        state.playbackStartDateTime,
                        DateTime.now(),
                    )
                    .plus({
                        milliseconds:
                            state.playbackElapsedTimeInMs * state.playbackSpeed,
                    })
                    .toISO() as string;
            }

            state.userRequestedPlayMode =
                state.userRequestedPlayMode === "pause" ? "resume" : "play";
            state.playbackStartDateTime = newStartPos;
            state.playbackElapsedTimeInMs = 0;
        },
        saveCurrentPlaybackPosition: (state) => {
            const currentPlaybackEndDateTime = luxonUtils
                .fromIsoWithFallback(
                    state.playbackStartDateTime,
                    DateTime.now(),
                )
                .plus({
                    milliseconds:
                        state.playbackElapsedTimeInMs * state.playbackSpeed,
                });

            state.playbackStartDateTime =
                currentPlaybackEndDateTime.toISO() as string;
            state.playbackElapsedTimeInMs = 0;
            state.playbackStartDateTimeChangedTimestamp =
                DateTime.now().toUnixInteger();
        },
        setPlaybackStartDateTime: (
            state,
            action: PayloadAction<{ playbackStartDateTime: string }>,
        ) => {
            // new values
            state.playbackStartDateTime = action.payload.playbackStartDateTime;
            state.playbackElapsedTimeInMs = 0;
            state.playbackStartDateTimeChangedTimestamp =
                DateTime.now().toUnixInteger();

            if (state.userRequestedPlayMode === "pause") {
                state.userRequestedPlayMode = "stop";
            }

            // reset buffering states
            state.persistedRecordingsReadyForPlayback = [];
            state.persistedRecordingsWaiting = [];
            state.persistedRecordingsFailedToLoad = [];
            state.reversedPersistedRecordingsReadyForPlayback = [];
            state.reversedPersistedRecordingsWaiting = [];
            state.reversedPersistedRecordingsFailedToLoad = [];
        },
        setPlaybackSpeed: (state, action: PayloadAction<{ speed: number }>) => {
            state.playbackStartDateTime = luxonUtils
                .fromIsoWithFallback(
                    state.playbackStartDateTime,
                    DateTime.now(),
                )
                .plus({
                    milliseconds:
                        state.playbackElapsedTimeInMs * state.playbackSpeed,
                })
                .toISO() as string;
            state.playbackElapsedTimeInMs = 0;
            state.playbackStartDateTimeChangedTimestamp =
                DateTime.now().toUnixInteger();
            state.playbackSpeed = action.payload.speed;
        },
        pausePlayback: (state) => {
            state.userRequestedPlayMode = "pause";
        },
        stopPlayback: (state) => {
            state.userRequestedPlayMode = "stop";
            state.playbackStartDateTime = undefined;
        },
        addSecondsToPlaybackStartDateTime: (
            state,
            action: PayloadAction<{ seconds: number }>,
        ) => {
            const newDateTime = luxonUtils
                .fromIsoWithFallback(
                    state.playbackStartDateTime,
                    DateTime.now(),
                )
                .plus({ seconds: action.payload.seconds });

            state.playbackStartDateTime = newDateTime.toISO() as string;
            state.playbackElapsedTimeInMs = 0;
            state.playbackStartDateTimeChangedTimestamp =
                DateTime.now().toUnixInteger();
        },
        subtractSecondsFromPlaybackStartDateTime: (
            state,
            action: PayloadAction<{ seconds: number }>,
        ) => {
            const newDateTime = luxonUtils
                .fromIsoWithFallback(
                    state.playbackStartDateTime,
                    DateTime.now(),
                )
                .minus({ seconds: action.payload.seconds });

            state.playbackStartDateTime = newDateTime.toISO() as string;
            state.playbackElapsedTimeInMs = 0;
            state.playbackStartDateTimeChangedTimestamp =
                DateTime.now().toUnixInteger();
        },
        setPersistedRecordingReadyForPlayback: (
            state,
            action: PayloadAction<{ persistedRecordingId: EntityId }>,
        ) => {
            state.persistedRecordingsReadyForPlayback.push(
                action.payload.persistedRecordingId,
            );

            state.persistedRecordingsFailedToLoad =
                state.persistedRecordingsFailedToLoad.filter(
                    (x) => x !== action.payload.persistedRecordingId,
                );
            state.persistedRecordingsWaiting =
                state.persistedRecordingsWaiting.filter(
                    (x) => x !== action.payload.persistedRecordingId,
                );
        },
        setPersistedRecordingFailedToLoad: (
            state,
            action: PayloadAction<{ persistedRecordingId: EntityId }>,
        ) => {
            state.persistedRecordingsFailedToLoad.push(
                action.payload.persistedRecordingId,
            );
            state.persistedRecordingsReadyForPlayback =
                state.persistedRecordingsReadyForPlayback.filter(
                    (x) => x !== action.payload.persistedRecordingId,
                );
            state.persistedRecordingsWaiting =
                state.persistedRecordingsWaiting.filter(
                    (x) => x !== action.payload.persistedRecordingId,
                );
        },
        setPersistedRecordingWaiting: (
            state,
            action: PayloadAction<{ persistedRecordingId: EntityId }>,
        ) => {
            state.persistedRecordingsWaiting.push(
                action.payload.persistedRecordingId,
            );
            state.persistedRecordingsReadyForPlayback =
                state.persistedRecordingsReadyForPlayback.filter(
                    (x) => x !== action.payload.persistedRecordingId,
                );
        },
        setReversedPersistedRecordingReadyForPlayback: (
            state,
            action: PayloadAction<{ persistedRecordingId: EntityId }>,
        ) => {
            state.reversedPersistedRecordingsReadyForPlayback.push(
                action.payload.persistedRecordingId,
            );

            state.reversedPersistedRecordingsFailedToLoad =
                state.reversedPersistedRecordingsFailedToLoad.filter(
                    (x) => x !== action.payload.persistedRecordingId,
                );
            state.reversedPersistedRecordingsWaiting =
                state.reversedPersistedRecordingsWaiting.filter(
                    (x) => x !== action.payload.persistedRecordingId,
                );
        },
        setReversedPersistedRecordingFailedToLoad: (
            state,
            action: PayloadAction<{ persistedRecordingId: EntityId }>,
        ) => {
            state.reversedPersistedRecordingsFailedToLoad.push(
                action.payload.persistedRecordingId,
            );
            state.reversedPersistedRecordingsReadyForPlayback =
                state.reversedPersistedRecordingsReadyForPlayback.filter(
                    (x) => x !== action.payload.persistedRecordingId,
                );
            state.reversedPersistedRecordingsWaiting =
                state.reversedPersistedRecordingsWaiting.filter(
                    (x) => x !== action.payload.persistedRecordingId,
                );
        },
        setReversedPersistedRecordingWaiting: (
            state,
            action: PayloadAction<{ persistedRecordingId: EntityId }>,
        ) => {
            state.reversedPersistedRecordingsWaiting.push(
                action.payload.persistedRecordingId,
            );
            state.reversedPersistedRecordingsReadyForPlayback =
                state.reversedPersistedRecordingsReadyForPlayback.filter(
                    (x) => x !== action.payload.persistedRecordingId,
                );
        },
        setPersistedRecordingsDownloadStatus: (
            state,
            action: PayloadAction<PersistedRecordingStatusChanged[]>,
        ) => {
            const updates = action.payload.map((x) => ({
                id: x.persistedRecordingId,
                changes: { status: x.downloadStatus },
            }));

            persistedRecordingAdapter.updateMany(
                state.persistedRecordings,
                updates,
            );
        },
        setPersistedRecordingPersistedStartAndStopTime: (
            state,
            action: PayloadAction<PersistedRecordingPersistedStartAndStopTimeChanged>,
        ) => {
            persistedRecordingAdapter.updateOne(state.persistedRecordings, {
                id: action.payload.persistedRecordingId,
                changes: {
                    persistedStartTime: action.payload.startTime,
                    persistedStopTime: action.payload.stopTime,
                },
            });
        },
        setDossierDeviceStatus: (
            state,
            action: PayloadAction<DossierDeviceStatusChanged>,
        ) => {
            const device = state.dossier?.dossierDevices.find(
                (x) => x.id === action.payload.dossierDeviceId,
            );

            if (device) {
                device.status = action.payload.status;
            }
        },
        setDossierInformation: (
            state,
            action: PayloadAction<DossierInformationChanged>,
        ) => {
            if (state.dossier?.id === action.payload.dossierId) {
                state.dossier = {
                    ...state.dossier,
                    ...action.payload,
                };
            }
        },
        resetFailedToLoadRecording: (
            state,
            action: PayloadAction<{ persistedRecordingId: EntityId }>,
        ) => {
            state.persistedRecordingsFailedToLoad =
                state.persistedRecordingsFailedToLoad.filter(
                    (x) => x !== action.payload.persistedRecordingId,
                );
            state.reversedPersistedRecordingsFailedToLoad =
                state.reversedPersistedRecordingsFailedToLoad.filter(
                    (x) => x !== action.payload.persistedRecordingId,
                );
        },

        // debug
        initDebugSettingsUsingUserSettings: (
            state,
            action: PayloadAction<{ userSettings: UserSettings }>,
        ) => {
            state.debugShowBufferingRecordings =
                state.debugShowBufferingRecordings ??
                action.payload.userSettings
                    .debugToolsShowBufferingRecordingsByDefault;
            state.debugShowForwardAndReversedRecordings =
                state.debugShowForwardAndReversedRecordings ??
                action.payload.userSettings
                    .debugToolsShowForwardAndReversedRecordingsByDefault;
            state.debugShowRecordingsOutsidePlaybackAndBufferingWindow =
                state.debugShowRecordingsOutsidePlaybackAndBufferingWindow ??
                action.payload.userSettings
                    .debugToolsShowRecordingsOutsidePlaybackAndBufferingWindowByDefault;
            state.debugShowStatistics =
                state.debugShowStatistics ??
                action.payload.userSettings.debugToolsShowStatisticsByDefault;
            state.debugShowSystemControls =
                state.debugShowSystemControls ??
                action.payload.userSettings
                    .debugToolsShowSystemControlsByDefault;
            state.debugShowVideoDirectionInformation =
                state.debugShowVideoDirectionInformation ??
                action.payload.userSettings
                    .debugToolsShowVideoDirectionInformationByDefault;
        },
        toggleDebugShowStatistics: (state) => {
            state.debugShowStatistics = !state.debugShowStatistics;
        },
        toggleDebugShowBufferingRecordings: (state) => {
            state.debugShowBufferingRecordings =
                !state.debugShowBufferingRecordings;
        },
        toggleDebugShowRecordingsOutsidePlaybackAndBufferingWindow: (state) => {
            state.debugShowRecordingsOutsidePlaybackAndBufferingWindow =
                !state.debugShowRecordingsOutsidePlaybackAndBufferingWindow;
        },
        toggleDebugShowForwardAndReversedRecordings: (state) => {
            state.debugShowForwardAndReversedRecordings =
                !state.debugShowForwardAndReversedRecordings;
        },
        toggleDebugShowSystemControls: (state) => {
            state.debugShowSystemControls = !state.debugShowSystemControls;
        },
        toggleDebugShowVideoDirectionInformation: (state) => {
            state.debugShowVideoDirectionInformation =
                !state.debugShowVideoDirectionInformation;
        },
        setFullscreenDeviceId: (
            state,
            action: PayloadAction<{ dossierDeviceId: EntityId | undefined }>,
        ) => {
            state.fullscreenDossierDeviceId = action.payload.dossierDeviceId;
        },
    },
    extraReducers(builder) {
        builder.addCase(loadDossierAsync.pending, (state) => {
            state.isLoadingDossier = true;
            state.errorLoadingDossier = undefined;
            state.dossier = undefined;
        });
        builder.addCase(loadDossierAsync.fulfilled, (state, action) => {
            state.isLoadingDossier = false;
            state.errorLoadingDossier = undefined;
            state.dossier = action.payload;
        });
        builder.addCase(loadDossierAsync.rejected, (state, action) => {
            state.isLoadingDossier = false;
            state.dossier = undefined;
            state.errorLoadingDossier = action.payload as string;
        });

        builder.addCase(
            fetchPersistedRecordingsForDossierDeviceAsync.fulfilled,
            (state, action) => {
                persistedRecordingAdapter.addMany(
                    state.persistedRecordings,
                    action.payload,
                );
            },
        );
        builder.addCase(
            removePersistedRecordingsAndStateForInvisibleDossierDevicesAsync.fulfilled,
            (state, action) => {
                // remove recordings
                persistedRecordingAdapter.removeMany(
                    state.persistedRecordings,
                    action.payload,
                );

                // remove recording state
                state.persistedRecordingsReadyForPlayback =
                    state.persistedRecordingsReadyForPlayback.filter(
                        (x) => !action.payload.includes(x),
                    );

                state.persistedRecordingsWaiting =
                    state.persistedRecordingsWaiting.filter(
                        (x) => !action.payload.includes(x),
                    );

                state.persistedRecordingsFailedToLoad =
                    state.persistedRecordingsFailedToLoad.filter(
                        (x) => !action.payload.includes(x),
                    );
            },
        );
    },
});

// exports
export const caseViewerThunks = {
    loadDossierAsync,
    fetchPersistedRecordingsForDossierDeviceAsync,
    removePersistedRecordingsAndStateForInvisibleDossierDevicesAsync,
};

export const caseViewerActions = caseViewerSlice.actions;

export default caseViewerSlice.reducer;
